import { useEffect, useRef, useState } from "react";
import GeneralService from "../../../service/general-service";
import "./birthplace-field-style.scss";

//—— 出生地欄位 ——
//
//  打關鍵字 → 後端回候選清單 → 選一個。選定的值帶著經緯度，
//  因為排盤要算「真太陽時」：同一個時鐘時間，在台北和在喀什，
//  太陽位置差快兩小時，時柱會整個不一樣。
//
//  放在 custom/ 而不是某支產品底下：鏡月（紫微）、緋紅（合盤）同樣要問出生地。
//
//  為什麼不把地名資料塞進前端：world-regions.json 有 600KB，
//  為了一個欄位讓每個使用者都下載它不划算。搜尋在後端只要 1~4ms。
//
//  props:
//    value            已選定的 { label, lat, lng }，沒選是 null
//    onChange         選定或清掉時呼叫
//    onPendingChange  選用。打了字但還沒從清單裡選中時回報 true ——
//                     外面用它決定「能不能送出」（見下方 pending 那段）
//    placeholder      範例文字

//打字停多久才送出搜尋。太短會每個按鍵都打一次 API，
//  太長則會覺得「打完了怎麼還沒反應」。
const DEBOUNCE_MS = 300;

//少於兩個字不搜：「市」這種單字會命中幾千筆，回來的清單沒有參考價值。
//  這個門檻要和後端一致（utils/birthplace-search.js 也擋兩個字）。
const MIN_QUERY = 2;

const BirthplaceField = ({
  value,
  onChange,
  onPendingChange,
  placeholder = "台北市",
}) => {
  //輸入框裡的字。和 value 是兩回事 ——
  //  使用者可能打了「彰化」但還沒從清單裡選，那時 value 還是 null。
  const [query, setQuery] = useState(value?.label || "");

  //—— 外面後來才給值時，要把字補上 ——
  //
  //  ⚠️ useState 的初始值**只在第一次掛載時讀一次**。個人資料編輯頁的
  //  value 是在 effect 裡灌進來的（useAuth 的 user 從 localStorage 來，
  //  這一頁可能在它同步好之前就掛上了），所以掛載當下 value 還是 null ——
  //  之後 value 變成「臺灣 彰化縣 彰化市」，這個輸入框卻永遠停在空字串。
  //
  //  症狀是「出生地明明存進資料庫了，重新打開卻是空的」，而且
  //  姓名、生日、時間都正常（那三個是 value={state} 直接綁的受控欄位，
  //  沒有自己的內部狀態）—— 看起來只有這一個欄位沒存到。
  //
  //  只在 value 有 label 時才覆寫：使用者正在打字時 value 是 null
  //  （見 handleInput），那時候不能把他打的字蓋掉。
  useEffect(() => {
    if (value?.label) setQuery(value.label);
  }, [value?.label]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  //—— 打了字，但還沒從清單裡選中 ——
  //
  //  這個欄位有兩個狀態：query（打的字）與 value（選定的地點，帶經緯度）。
  //  打「地球」這種搜不到的字時 value 還是 null，而外面只看得到 value ——
  //  所以外面會以為「使用者只是沒填」，照樣放行儲存，結果存進去的是空的
  //  出生地（Kevin 2026-10-30 回報：找不到這個地方卻還能按儲存）。
  //
  //  只有「選中的那一個」才算數 —— 排盤要的是經緯度（真太陽時校正），
  //  地名本身沒有用。所以 query 跟 value.label 不一致就是還沒選定。
  const pending =
    query.trim() !== "" && query.trim() !== (value?.label || "");

  //  用 effect 回報而不是在 render 裡直接呼叫：render 期間不能動外面的狀態。
  //  onPendingChange 進依賴不會造成迴圈 —— 外面即使傳 inline 箭頭函式，
  //  setState 收到相同的值時 React 會自己中止，不會再觸發一輪。
  useEffect(() => {
    onPendingChange?.(pending);
  }, [pending, onPendingChange]);

  //每次搜尋的序號。回應是非同步的，慢的請求可能比快的晚回來 ——
  //  沒有這個序號的話，「彰」的結果會蓋掉「彰化」的結果。
  const seq = useRef(0);

  useEffect(() => {
    const q = query.trim();

    //已經選定而且沒再改字 → 不用再搜
    if (value && q === value.label) {
      setItems([]);
      return;
    }

    if (q.length < MIN_QUERY) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const mySeq = ++seq.current;

    const timer = setTimeout(async () => {
      try {
        const res = await GeneralService.birthplaceSearch(q);
        //慢回應不要蓋掉新結果
        if (mySeq !== seq.current) return;
        setItems(res.data?.data || []);
        setOpen(true);
      } catch (e) {
        if (mySeq !== seq.current) return;
        //搜不到就當沒有候選 —— 這裡不該跳錯誤打斷填表，
        //使用者換個關鍵字就好。
        setItems([]);
      } finally {
        if (mySeq === seq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, value]);

  const handleInput = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    //改了字就等於放棄原本選的那個地點 ——
    //  不清掉的話，畫面顯示「彰化」但送出去的還是上次選的「臺北市」。
    if (value) onChange(null);
  };

  const handlePick = (item) => {
    onChange(item);
    setQuery(item.label);
    setItems([]);
    setOpen(false);
  };

  const showList = open && items.length > 0;

  return (
    <div className="bp-field">
      {/* 清單浮在輸入框上方而不是下方：這個欄位貼著畫面底部，
          往下展開會被鍵盤蓋住。 */}
      {showList && (
        <ul className="bp-field__list">
          {items.map((item) => (
            <li key={item.label}>
              <button
                type="button"
                className="bp-field__item"
                onClick={() => handlePick(item)}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* 不另外標示「已選定」—— 右邊的「確認」按鈕從壓暗變成可按，
          那就是最清楚的訊號了，欄位本身不用再加底線或換色。 */}
      <input
        className="bp-field__input"
        type="text"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        onChange={handleInput}
        onFocus={() => items.length > 0 && setOpen(true)}
      />

      {/* 狀態只在「有必要說」的時候出現：
          搜尋中、以及打了字卻沒有任何結果。已經選好了就不用再說什麼。 */}
      {!value && loading && <p className="bp-field__note">搜尋中…</p>}
      {!value &&
        !loading &&
        query.trim().length >= MIN_QUERY &&
        items.length === 0 && (
          <p className="bp-field__note">找不到這個地方</p>
        )}
    </div>
  );
};

export default BirthplaceField;
